"use client";

import { type FC, type ReactNode } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import { agreement } from "@const/agreement";
import Translations from "@translate/components/SignInIntro/IntroPrimaryAuthCta";
import { IntroFreeCheckupChip } from "./IntroFreeCheckupChip";
import type { IntroAuthPlatform } from "./resolveIntroAuthPlatform";

const I18n = I18nUtil.createScoped(Translations);

export interface IntroPrimaryAuthCtaProps {
  platform: IntroAuthPlatform;
  slideIndex: number;
  onApple?: () => void;
  onGoogle?: () => void;
  onGetStarted?: () => void;
  /** App：Other options → Sign in；PC 不渲染 */
  onOtherOptions?: () => void;
  termsSlot?: ReactNode;
  className?: string;
}

const PRIMARY_BUTTON_CLASS = cn(
  "button flex items-center justify-center gap-1 rounded-lg shrink-0",
  "bg-background_solid_neutral_primary text-content_primary",
  "button_l h-12 w-full max-w-[361px] px-4 py-3",
  "md:h-14 md:w-auto md:min-w-[80px] md:px-10",
);

function openAgreementUrl(url: string) {
  if (typeof window === "undefined") {
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/** 条款纯文案链（非 Checkbox）— audit #12 / fe-spec §4.6 */
export const IntroAuthTermsRow: FC<{ className?: string }> = ({
  className,
}) => (
  <p
    className={cn("body_s w-full text-content_inverse_primary pt-1", className)}
    data-testid="intro-auth-terms"
    data-node-id="102939:58231"
  >
    {I18n.t("By continuing, you agree to the")}{" "}
    <button
      type="button"
      className="underline decoration-solid"
      data-openreplay-label="intro_terms_of_use"
      onClick={() => openAgreementUrl(agreement.termsLink)}
    >
      {I18n.t("Terms of Use")}
    </button>{" "}
    {I18n.t("and")}{" "}
    <button
      type="button"
      className="underline decoration-solid"
      data-openreplay-label="intro_privacy_policy"
      onClick={() => openAgreementUrl(agreement.privacyLink)}
    >
      {I18n.t("Privacy Policy")}
    </button>
    .
  </p>
);

/**
 * Intro 底栏主 CTA — iOS Apple / Android Google / PC Get Started
 * Figma: 102803:53571 / 53620 / PC 102839:40348
 */
export const IntroPrimaryAuthCta: FC<IntroPrimaryAuthCtaProps> = ({
  platform,
  slideIndex,
  onApple,
  onGoogle,
  onGetStarted,
  onOtherOptions,
  termsSlot,
  className,
}) => {
  const isApp = platform === "ios" || platform === "android";
  const primaryLabel =
    platform === "ios"
      ? I18n.t("Continue with Apple")
      : platform === "android"
        ? I18n.t("Continue with Google")
        : I18n.t("Get Started");

  return (
    <div
      className={cn(
        "absolute bottom-0 left-0 z-20 w-full px-4 pb-6 pb-safe",
        "md:bottom-10 md:left-[72px] md:w-auto md:max-w-none md:px-0 md:pb-0",
        className,
      )}
      data-testid="intro-primary-auth-cta"
      data-platform={platform}
      data-slide-index={slideIndex}
    >
      {isApp ? (
        <div className="mx-auto flex w-full max-w-[361px] flex-col items-center gap-2">
          <IntroFreeCheckupChip layout="app" />
          {platform === "ios" ? (
            <button
              type="button"
              data-testid="intro-primary-auth-button"
              data-openreplay-label="intro_continue_apple"
              onClick={onApple}
              className={PRIMARY_BUTTON_CLASS}
            >
              <Icon
                name="apple"
                size={24}
                className="shrink-0 text-content_primary"
              />
              <span>{primaryLabel}</span>
            </button>
          ) : (
            <button
              type="button"
              data-testid="intro-primary-auth-button"
              data-openreplay-label="intro_continue_google"
              onClick={onGoogle}
              className={PRIMARY_BUTTON_CLASS}
            >
              <Icon name="Google with colour" size={24} className="shrink-0" />
              <span>{primaryLabel}</span>
            </button>
          )}
          <button
            type="button"
            data-testid="intro-other-options"
            data-openreplay-label="intro_other_options"
            onClick={onOtherOptions}
            className={cn(
              "button_m flex h-12 w-full items-center justify-center rounded-lg px-4",
              "text-content_inverse_primary",
            )}
          >
            {I18n.t("Other options")}
          </button>
          {termsSlot ?? <IntroAuthTermsRow />}
        </div>
      ) : (
        <div
          className="mx-auto flex w-full max-w-[361px] flex-col items-center gap-2 md:mx-0 md:w-auto md:max-w-none md:flex-row md:items-center md:gap-3"
          data-node-id="102839:40347"
          data-testid="intro-pc-cta-row"
        >
          {/* H5：chip 在按钮上方（稿 102803:53565）；md+：chip 在按钮右侧 */}
          <div className="md:hidden" data-testid="intro-free-checkup-chip-h5">
            <IntroFreeCheckupChip layout="app" />
          </div>
          <button
            type="button"
            data-testid="intro-get-started-cta"
            data-openreplay-label="intro_get_started"
            onClick={onGetStarted}
            className={PRIMARY_BUTTON_CLASS}
          >
            {primaryLabel}
          </button>
          <div
            className="hidden md:block"
            data-testid="intro-free-checkup-chip-pc"
          >
            <IntroFreeCheckupChip layout="pc" />
          </div>
        </div>
      )}
    </div>
  );
};
